import type {
  Entitlement,
  KnowledgeResource,
  MarketplaceOrder,
  ServiceCategory,
  ServiceProvider,
} from '../types/marketplace'

export const serviceCategories: ServiceCategory[] = [
  {
    id: 'cat-1',
    category: 'Academic support',
    subcategories: ['Tutoring', 'Research assistance', 'Statistics', 'Proofreading'],
    configurable: true,
  },
  {
    id: 'cat-2',
    category: 'Admissions and study abroad',
    subcategories: ['Application guidance', 'Personal statement review', 'IELTS coaching', 'Interview preparation'],
    configurable: true,
  },
  {
    id: 'cat-3',
    category: 'Creative and technical',
    subcategories: ['Graphic design', 'Programming', 'Video editing', 'CAD', 'Data analysis'],
    configurable: true,
  },
  {
    id: 'cat-4',
    category: 'Career',
    subcategories: ['CV review', 'Portfolio review', 'Mock interviews', 'Mentorship'],
    configurable: true,
  },
]

export const knowledgeResources: KnowledgeResource[] = [
  {
    id: 'res-k1',
    title: 'Control Systems Engineering — Nigerian edition',
    author: 'F. Adeyemi',
    publisher: 'Akoka Academic Press',
    kind: 'Textbook',
    edition: '4th edition, 2025',
    field: 'Engineering',
    pagesOrLength: '612 pages',
    rating: 4.7,
    reviewCount: 214,
    authorVerified: true,
    licences: [
      { model: 'rental', priceNaira: 2500, rentalDays: 30, preview: true, download: false, print: false, redistribute: false, territories: 'Nigeria only' },
      { model: 'purchase', priceNaira: 11500, preview: true, download: true, print: true, redistribute: false, territories: 'Worldwide' },
    ],
  },
  {
    id: 'res-k2',
    title: 'Operations Research II — past questions 2018–2025',
    author: 'Systems Engineering Students Association',
    kind: 'Past questions',
    edition: 'Compiled 2026',
    field: 'Engineering',
    pagesOrLength: '96 pages',
    rating: 4.4,
    reviewCount: 88,
    authorVerified: true,
    licences: [
      { model: 'free', priceNaira: 0, preview: true, download: true, print: true, redistribute: false, territories: 'Worldwide' },
    ],
  },
  {
    id: 'res-k3',
    title: 'Applied Statistics for Engineers — video course',
    author: 'K. Eze',
    kind: 'Video course',
    edition: '2026 cohort recording',
    field: 'Mathematics',
    pagesOrLength: '14 hours',
    rating: 4.8,
    reviewCount: 132,
    authorVerified: true,
    licences: [
      { model: 'rental', priceNaira: 4000, rentalDays: 60, preview: true, download: false, print: false, redistribute: false, territories: 'Worldwide' },
    ],
  },
  {
    id: 'res-k4',
    title: 'Grid integration of distributed solar in West Africa',
    author: 'A. Bello, O. Nwachukwu',
    publisher: 'Journal of African Energy Systems',
    kind: 'Research paper',
    edition: 'Vol 12, 2026',
    field: 'Energy systems',
    pagesOrLength: '28 pages',
    rating: 4.2,
    reviewCount: 19,
    authorVerified: true,
    licences: [
      { model: 'purchase', priceNaira: 3500, preview: true, download: true, print: false, redistribute: false, territories: 'Worldwide' },
    ],
  },
  {
    id: 'res-k5',
    title: 'Database Systems — annotated lecture notes',
    author: 'B. Salami',
    kind: 'Lecture notes',
    edition: 'Semester 1, 2026',
    field: 'Computing',
    pagesOrLength: '148 pages',
    rating: 4.5,
    reviewCount: 61,
    authorVerified: true,
    licences: [
      { model: 'free', priceNaira: 0, preview: true, download: true, print: false, redistribute: false, territories: 'Institution only' },
    ],
  },
  {
    id: 'res-k6',
    title: 'Writing a competitive master’s motivation letter',
    author: 'T. Okoro',
    kind: 'Course pack',
    edition: '2026',
    field: 'Study abroad',
    pagesOrLength: '64 pages + templates',
    rating: 4.6,
    reviewCount: 47,
    authorVerified: false,
    licences: [
      { model: 'rental', priceNaira: 1500, rentalDays: 14, preview: true, download: false, print: false, redistribute: false, territories: 'Worldwide' },
      { model: 'purchase', priceNaira: 6000, preview: true, download: true, print: true, redistribute: false, territories: 'Worldwide' },
    ],
  },
]

export const myEntitlements: Entitlement[] = [
  {
    resourceId: 'res-k1',
    model: 'rental',
    grantedOn: '28 Jul 2026',
    expiresOn: '27 Aug 2026',
    state: 'active',
  },
  {
    resourceId: 'res-k2',
    model: 'free',
    grantedOn: '14 Jul 2026',
    state: 'active',
  },
  {
    resourceId: 'res-k3',
    model: 'rental',
    grantedOn: '2 Jun 2026',
    expiresOn: '1 Aug 2026',
    state: 'expired',
  },
]

export const serviceProviders: ServiceProvider[] = [
  {
    id: 'prov-1',
    handle: 'tokoro',
    displayName: 'Tosin Okoro',
    initials: 'TO',
    headline: 'Study-abroad applications and motivation letters',
    category: 'Admissions and study abroad',
    subcategory: 'Personal statement review',
    skills: ['Motivation letters', 'Scholarship essays', 'European admissions'],
    mentorship: true,
    responseTime: 'Replies in about 3 hours',
    completedOrders: 214,
    rating: 4.9,
    reviewCount: 186,
    identityVerified: true,
    availabilityLabel: 'Next session Fri 14 Aug, 16:00',
    packages: [
      { id: 'pk-1', name: 'Motivation letter review', priceNaira: 18000, deliveryDays: 3, includes: ['Line edits', 'Structural feedback', 'One revision round'] },
      { id: 'pk-2', name: 'Full application review', priceNaira: 45000, deliveryDays: 7, includes: ['Letter', 'CV', 'Programme fit check', 'Two revision rounds'] },
      { id: 'pk-3', name: '45-minute strategy call', priceNaira: 12000, sessionMinutes: 45, includes: ['Shortlist review', 'Deadline plan'] },
    ],
  },
  {
    id: 'prov-2',
    handle: 'ifeoma.e',
    displayName: 'Ifeoma Eze',
    initials: 'IE',
    headline: 'Engineering mathematics and statistics tutoring',
    category: 'Academic support',
    subcategory: 'Tutoring',
    skills: ['Linear algebra', 'Probability', 'MATLAB'],
    mentorship: false,
    responseTime: 'Replies in about 1 hour',
    completedOrders: 96,
    rating: 4.8,
    reviewCount: 74,
    identityVerified: true,
    availabilityLabel: 'Weekday evenings',
    packages: [
      { id: 'pk-4', name: '60-minute tutorial', priceNaira: 7000, sessionMinutes: 60, includes: ['Problem walkthrough', 'Practice set'] },
      { id: 'pk-5', name: 'Weekly tutoring — 4 sessions', priceNaira: 25000, sessionMinutes: 60, includes: ['Four sessions', 'Progress notes'] },
    ],
  },
  {
    id: 'prov-3',
    handle: 'danielu',
    displayName: 'Daniel Uzo',
    initials: 'DU',
    headline: 'Frontend development and technical portfolios',
    category: 'Creative and technical',
    subcategory: 'Programming',
    skills: ['React', 'TypeScript', 'Portfolio builds'],
    mentorship: true,
    responseTime: 'Replies in about 6 hours',
    completedOrders: 41,
    rating: 4.6,
    reviewCount: 33,
    identityVerified: true,
    availabilityLabel: 'Two order slots this week',
    packages: [
      { id: 'pk-6', name: 'Portfolio site build', priceNaira: 85000, deliveryDays: 10, includes: ['Three pages', 'Responsive', 'Deployment'] },
      { id: 'pk-7', name: '30-minute code review', priceNaira: 6000, sessionMinutes: 30, includes: ['Repo walkthrough', 'Written notes'] },
    ],
  },
  {
    id: 'prov-4',
    handle: 'gracen',
    displayName: 'Grace Nnamdi',
    initials: 'GN',
    headline: 'CV, LinkedIn and interview preparation for graduates',
    category: 'Career',
    subcategory: 'CV review',
    skills: ['CV writing', 'Mock interviews', 'Graduate schemes'],
    mentorship: true,
    responseTime: 'Replies in about 2 hours',
    completedOrders: 158,
    rating: 4.7,
    reviewCount: 121,
    identityVerified: true,
    availabilityLabel: 'Next session Mon 17 Aug, 10:00',
    packages: [
      { id: 'pk-8', name: 'CV rewrite', priceNaira: 15000, deliveryDays: 4, includes: ['Full rewrite', 'ATS check'] },
      { id: 'pk-9', name: 'Mock interview — 60 minutes', priceNaira: 20000, sessionMinutes: 60, includes: ['Live interview', 'Scored feedback'] },
      { id: 'pk-10', name: 'Monthly career mentorship', priceNaira: 40000, sessionMinutes: 60, includes: ['Two sessions a month', 'Async questions'] },
    ],
  },
  {
    id: 'prov-5',
    handle: 'seunprints',
    displayName: 'Seun Adigun',
    initials: 'SA',
    headline: 'Project posters, CAD drawings and technical diagrams',
    category: 'Creative and technical',
    subcategory: 'CAD',
    skills: ['AutoCAD', 'SolidWorks', 'Technical illustration'],
    mentorship: false,
    responseTime: 'Replies in about 12 hours',
    completedOrders: 12,
    rating: 4.1,
    reviewCount: 9,
    identityVerified: false,
    availabilityLabel: 'Accepting orders',
    packages: [
      { id: 'pk-11', name: 'Single CAD drawing', priceNaira: 9000, deliveryDays: 3, includes: ['One drawing', 'Two revisions'] },
    ],
  },
]

export const myOrders: MarketplaceOrder[] = [
  {
    id: 'ord-1',
    reference: 'ORD-40128',
    vertical: 'services',
    title: 'Motivation letter review — TU Delft SET',
    counterparty: 'Tosin Okoro',
    counterpartyHandle: 'tokoro',
    amountNaira: 18000,
    feeNaira: 900,
    state: 'IN_PROGRESS',
    placed: '10 Aug 2026',
    dueLabel: 'Delivery due 13 Aug',
    history: [
      { state: 'REQUESTED', at: '9 Aug, 18:20' },
      { state: 'ACCEPTED', at: '9 Aug, 20:04' },
      { state: 'FUNDED', at: '10 Aug, 08:12' },
      { state: 'IN_ESCROW', at: '10 Aug, 08:12' },
      { state: 'IN_PROGRESS', at: '10 Aug, 09:30' },
    ],
  },
  {
    id: 'ord-2',
    reference: 'ORD-39880',
    vertical: 'knowledge',
    title: 'Control Systems Engineering — 30 day rental',
    counterparty: 'Akoka Academic Press',
    counterpartyHandle: 'akokapress',
    amountNaira: 2500,
    feeNaira: 250,
    state: 'COMPLETED',
    placed: '28 Jul 2026',
    dueLabel: 'Access until 27 Aug',
    history: [
      { state: 'REQUESTED', at: '28 Jul, 11:02' },
      { state: 'FUNDED', at: '28 Jul, 11:03' },
      { state: 'COMPLETED', at: '28 Jul, 11:03' },
      { state: 'RELEASED', at: '4 Aug, 00:00' },
    ],
  },
  {
    id: 'ord-3',
    reference: 'ORD-39511',
    vertical: 'services',
    title: '60-minute tutorial — probability',
    counterparty: 'Ifeoma Eze',
    counterpartyHandle: 'ifeoma.e',
    amountNaira: 7000,
    feeNaira: 350,
    state: 'BUYER_REVIEW',
    placed: '7 Aug 2026',
    dueLabel: 'Approve by 14 Aug or it auto-completes',
    history: [
      { state: 'REQUESTED', at: '6 Aug, 15:10' },
      { state: 'ACCEPTED', at: '6 Aug, 15:44' },
      { state: 'FUNDED', at: '6 Aug, 16:00' },
      { state: 'IN_ESCROW', at: '6 Aug, 16:00' },
      { state: 'DELIVERED', at: '7 Aug, 19:05' },
      { state: 'BUYER_REVIEW', at: '7 Aug, 19:05' },
    ],
  },
  {
    id: 'ord-4',
    reference: 'ORD-38904',
    vertical: 'services',
    title: 'Single CAD drawing — heat exchanger',
    counterparty: 'Seun Adigun',
    counterpartyHandle: 'seunprints',
    amountNaira: 9000,
    feeNaira: 450,
    state: 'DISPUTED',
    placed: '22 Jul 2026',
    dueLabel: 'Under review by Campuzon trust and safety',
    history: [
      { state: 'REQUESTED', at: '20 Jul, 09:00' },
      { state: 'ACCEPTED', at: '20 Jul, 10:11' },
      { state: 'FUNDED', at: '20 Jul, 10:40' },
      { state: 'IN_ESCROW', at: '20 Jul, 10:40' },
      { state: 'DELIVERED', at: '22 Jul, 23:58' },
      { state: 'DISPUTED', at: '23 Jul, 08:15' },
    ],
  },
  {
    id: 'ord-5',
    reference: 'ORD-38610',
    vertical: 'events',
    title: 'Campuzon Hackathon — housing data track',
    counterparty: 'Campuzon Technologies',
    counterpartyHandle: 'campuzon',
    amountNaira: 2500,
    feeNaira: 0,
    state: 'FUNDED',
    placed: '11 Aug 2026',
    dueLabel: 'Ticket issued for 29 Aug',
    history: [
      { state: 'REQUESTED', at: '11 Aug, 12:00' },
      { state: 'FUNDED', at: '11 Aug, 12:01' },
    ],
  },
]

export function providerByHandle(handle: string) {
  return serviceProviders.find((provider) => provider.handle === handle)
}

export function resourceById(id: string) {
  return knowledgeResources.find((resource) => resource.id === id)
}
